import $ from 'jquery';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert, debugAssert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as browser from 'lib/Browser';
import localStorage from 'LocalStorage';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {MAX_STALE, SAVE_INTERVAL} from 'notebook/store/auto-save/AutoSaverConstants';
import {GetItemParts, ItemPartSaveTracker} from 'notebook/store/auto-save/ItemPartSaveTracker';
// @ts-expect-error
import drafts from 'notebook/store/data-recovery/Drafts';
import notifications, {Response} from 'notifications';
import EntityStore from 'stores/EntityStore';
import apiutil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {typeToName} from 'util/NameUtil';
import throttle from 'util/throttle';

const SIGNIN_URL = '/signin';

// Only log non-authentication-related errors.
function shouldClientDump(status: number | null, error: {csrfFailed?: boolean | null}): boolean {
  return (
    status != null && ![401, 403, 404].includes(status) && !(error != null ? error.csrfFailed : undefined)
  );
}

interface ItemStateFixedProperties {
  name?: string;
  openVersion: number;
  savedVersion: number;
  cidIdMap?: CidIdMap;
  lockedFromExternalChange?: boolean;
}

/**
 * The ItemState is an extension mechanism for the AutoSaver. It allows the instantiator of the AutoSaver to
 * determine what will be persisted to LocalStorage, if that is enabled. The ItemState itself is a Record
 * which contains an `itemKey` whose value is the actual item that will be persisted in LocalStorage along
 * with the `cidIdMap` and the `lockedFromExternalChange` values which are also contained on the
 * `ItemStateProps`. If the `ItemState` type variable is undefined, then we assume that there is no `itemKey`
 * and that there is no value which will be persisted to LocalStorage.
 */
type ItemStateProps<ItemState> = (ItemState extends undefined ? {} : Record<PropertyKey, ItemState>) &
  ItemStateFixedProperties;

interface AutoSaverParams<ItemState, ItemStatePart> {
  id: string;
  friendlyName: string;
  itemKey?: PropertyKey;
  doSave: ((isImmediate?: boolean) => JQueryPromise<boolean>) | null;
  getItemState: () => ItemStateProps<ItemState>;
  getItemParts?: GetItemParts<ItemState, ItemStatePart>;
  persistToLocalStorage: boolean;
  showErrors: boolean;
  checkItemLoaded: boolean;
}

interface SaveResponse extends Response {
  noResave?: boolean;
  csrfFailed?: boolean | null;
}

/**
 * Manager for automatically persisting data to the API and local storage every once in a while.
 * The enclosing class declares state version numbers through the getItemState callback (which
 * should be incremented every time the state changes in a substantial way), and this class checks
 * every 2 seconds if the version number changed, and invokes the doSave callback if so.
 */
class AutoSaver<ItemState = unknown, ItemStatePart = unknown> {
  private readonly id: string;
  private readonly friendlyName: string;
  private readonly itemKey?: PropertyKey;

  private _doSave: () => JQueryPromise<boolean>;
  private _getItemState: () => ItemStateProps<ItemState>;
  throttledQueueSave: () => void;

  private readonly persistToLocalStorage: boolean;
  private readonly showErrors: boolean;
  private readonly checkItemLoaded: boolean;
  private isStopped: boolean;
  private staleCount: number; // how many ticks have passed with the same lastSavedVersion
  private lastSavedVersion: number; // the value of item.savedVersion from the last tick

  // We initialize lsOpenVersion to the current openVersion, so that on page load, if the user hasn't
  // intended to make changes, we don't immediately blow away the localStorage data.
  private localStorageOpenVersion: number;
  private localStorageSavedVersion: number;

  private staleWarningShown: boolean; // if we have shown the warning because of staleCount > MAX_STALE
  private unsaveableWarningShown: boolean; // if we have shown the warning because of call to @pause()
  private unloadEvent: string;
  private resumeClickEvent: string;
  private draftId: string;
  private recoverWarningShown?: boolean;
  // an intervalID if the autosaver is ticking
  private tickId?: ReturnType<typeof setInterval> | null;
  private savePromiseChain: JQueryPromise<boolean> | null;
  private promiseChainLength: number = 0;

  public readonly itemPartSaveTracker: ItemPartSaveTracker<ItemState, ItemStatePart> | null;

  constructor({
    id,
    friendlyName,
    itemKey,
    doSave,
    getItemState,
    getItemParts,
    persistToLocalStorage,
    showErrors,
    checkItemLoaded,
  }: AutoSaverParams<ItemState, ItemStatePart>) {
    this.id = id;
    this.friendlyName = friendlyName;
    this.itemKey = itemKey;
    this._doSave = doSave ?? (() => $.Deferred<boolean>().resolve(true).promise());
    this._getItemState = getItemState;
    this.itemPartSaveTracker = getItemParts
      ? new ItemPartSaveTracker<ItemState, ItemStatePart>(getItemParts)
      : null;
    this.persistToLocalStorage = persistToLocalStorage;
    this.showErrors = showErrors;
    this.checkItemLoaded = checkItemLoaded;
    this.isStopped = false; // true after we've called stop()
    if (this.persistToLocalStorage == null) {
      this.persistToLocalStorage = true;
    }
    if (this.showErrors == null) {
      this.showErrors = true;
    }
    if (this.checkItemLoaded == null) {
      this.checkItemLoaded = true;
    }
    if (browser.isBrowser) {
      this.startTick();
    }
    this.staleCount = 0; // how many ticks have passed with the same lastSavedVersion
    const {openVersion, savedVersion} = this._getItemState();
    this.lastSavedVersion = savedVersion; // the value of item.savedVersion from the last tick

    // We initialize lsOpenVersion to the current openVersion, so that on page load, if the user hasn't
    // intended to make changes, we don't immediately blow away the localStorage data.
    this.localStorageOpenVersion = openVersion;
    this.localStorageSavedVersion = savedVersion;

    this.staleWarningShown = false; // if we have shown the warning because of staleCount > MAX_STALE
    this.unsaveableWarningShown = false; // if we have shown the warning because of call to @pause()
    this.unloadEvent = `beforeunload.auto-save-${this.id}`;
    this.resumeClickEvent = `click.auto-save-${this.id}`;
    this.draftId = drafts.draftId(this.itemKey, this.id);
    this.savePromiseChain = null;
    this.throttledQueueSave = throttle(() => {
      this.queueSave();
    }, SAVE_INTERVAL);

    if (this.persistToLocalStorage) {
      const localDraft = drafts.getLocalDrafts()[this.draftId];
      this.recoverWarningShown = drafts.shouldPromptForRecovery(localDraft);

      if (this.recoverWarningShown) {
        analytics.track('auto_saver', 'see_prompt', {file_api_id: this.id});
        notifications.warn(
          <>
            We recovered unsaved changes to your {this.friendlyFileNameWithStyling()}.{' '}
            <a href={drafts.RECOVERY_HASH}>Click here</a> to recover this data.
          </>,
          {duration: 0, id: drafts.RECOVERY_ID}
        );
      }
    }
    if (browser.isBrowser) {
      $(document).on(this.resumeClickEvent, `a[href='${this.resumeHash()}']`, (e) => {
        e.preventDefault();
        this.resume();
      });

      if (this.showErrors) {
        $(window).on(this.unloadEvent, () => this.shouldShowUnsavedMessage());
      }
    }
  }

  set doSave(doSave: (() => JQueryPromise<boolean>) | null) {
    this._doSave = doSave ?? (() => $.Deferred<boolean>().resolve(true).promise());
  }

  set getItemState(getItemState: () => ItemStateProps<ItemState>) {
    this._getItemState = getItemState;
  }

  private resumeHash(): string {
    return `#resume-autosave=${this.id}`;
  }

  private shouldShowUnsavedMessage(): boolean | undefined {
    const {savedVersion, openVersion} = this._getItemState();
    if (openVersion !== savedVersion || this.promiseChainLength > 0) {
      // Modern browsers do not allow sending a custom message
      return true;
    }
    // Returning undefined will not trigger the browser asking for confirmation before leaving the page.
    return undefined;
  }

  private getFriendlyNameComponents(): [string, string | undefined] {
    const itemTypeName = `${typeToName(apiutil.apiIdToType(this.id))}`;
    const itemState = this._getItemState();
    const item = this.itemKey ? (itemState as Record<PropertyKey, ItemState>)[this.itemKey] : undefined;
    const itemName: string | undefined = (item as {name?: string})?.name;
    return [itemTypeName, itemName];
  }

  friendlyFileName(): string {
    if (this.friendlyName != null) {
      return this.friendlyName;
    }
    const [itemTypeName, itemName] = this.getFriendlyNameComponents();

    let filename = '';
    if (itemName != null) {
      filename = `"${itemName}"`;
    }
    return `${itemTypeName} ${filename}`;
  }

  friendlyFileNameWithStyling(): JSX.Element {
    if (this.friendlyName != null) {
      return <>{this.friendlyName}</>;
    }

    const [itemTypeName, itemName] = this.getFriendlyNameComponents();
    let filename = <></>;
    if (itemName != null) {
      filename = <strong>"{itemName}"</strong>;
    }
    return (
      <>
        {itemTypeName} {filename}
      </>
    );
  }

  private saveToLocalStorage(): void {
    if (!this.persistToLocalStorage || !localStorage.enabled()) {
      return;
    }

    const itemState = this._getItemState();
    const item = this.itemKey ? (itemState as Record<PropertyKey, ItemState>)[this.itemKey] : null;
    const {openVersion, savedVersion, cidIdMap, lockedFromExternalChange} = itemState;

    // If the file has changed or the file is now up-to-date since we last saved, save to localStorage
    if (
      this.localStorageOpenVersion !== openVersion ||
      this.localStorageSavedVersion !== savedVersion ||
      lockedFromExternalChange
    ) {
      this.localStorageOpenVersion = openVersion;
      this.localStorageSavedVersion = savedVersion;

      const unsaved = openVersion !== savedVersion;
      const timestamp = new Date();

      const localDrafts = drafts.getLocalDrafts();
      if (unsaved) {
        localDrafts[this.draftId] = {
          timestamp,
          item,
          cidIdMap,
          unsaved,
          lockedFromExternalChange,
          author: EntityStore.currentUser().id,
          dismissed: false,
        };
      } else {
        delete localDrafts[this.draftId];
      }
      drafts.setLocalDrafts(localDrafts);

      // If we have overwritten unsaved changes, let the user know.
      if (this.recoverWarningShown) {
        this.recoverWarningShown = false;
        analytics.track('auto_save', 'discard', {file_id: this.id});
        notifications.warn('Unsaved changes have been discarded.', {id: drafts.RECOVERY_ID});
      }
    }
  }

  isTicking(): boolean {
    return this.tickId != null;
  }

  // Starts the tick if it is not in progress, returns true if the tick was just started.
  startTick(): boolean {
    if (this.isTicking()) {
      return false;
    }
    this.tickId = setInterval(() => {
      this.tick(true);
    }, SAVE_INTERVAL);
    return true;
  }

  // Stop the tick if it is in progress, returns true if the tick was just stopped.
  private stopTick(): boolean {
    if (!this.isTicking()) {
      return false;
    }
    if (this.tickId) {
      clearInterval(this.tickId);
    }
    this.tickId = null;
    this.staleCount = 0;
    return true;
  }

  private notificationId(): string {
    return `autoSaver-stale-${this.id}`;
  }

  private secondaryWarningId(): string {
    return `autoSaver-warn-${this.id}`;
  }

  private permanentWarn(message: string | JSX.Element): void {
    notifications.warn(message, {duration: 0, closeable: false, id: this.notificationId()});
  }

  private permanentError(message: string | JSX.Element): void {
    notifications.error(message, {duration: 0, closeable: false, id: this.notificationId()});
  }

  notifyRetry(status: object): void {
    analytics.track('auto_save', 'auto_retry', {file_id: this.id, status});
    const filename = this.friendlyFileNameWithStyling();
    if (this.showErrors) {
      this.permanentWarn(<>Experiencing server issues while saving {filename}, retrying save...</>);
    }
  }

  tick(shouldSave?: boolean): void {
    let filename;
    assert(this.isTicking(), 'tick() called without tick running.');

    const itemState = this._getItemState();
    const item = this.itemKey ? (itemState as Record<PropertyKey, ItemState>)[this.itemKey] : null;
    // If the item hasn't loaded yet, nothing to freak out about.
    if (this.checkItemLoaded && item == null) {
      return;
    }

    // Keep track of staleness
    // If the saved version has changed or we are up-to-date, we can reset the stale count.
    const {savedVersion, openVersion} = itemState;
    if (this.lastSavedVersion !== savedVersion || openVersion === savedVersion) {
      this.lastSavedVersion = savedVersion;

      // Only let the user know everything's okay if we had triggered a stale error message.
      if (this.staleWarningShown || this.unsaveableWarningShown) {
        filename = this.friendlyFileNameWithStyling();
        analytics.track('auto_save', 'reconnected', {file_id: this.id});
        if (openVersion === savedVersion) {
          notifications.success(<>Reconnected. Your {filename} saved successfully.</>, {
            id: this.notificationId(),
          });
          this.staleWarningShown = false;
        } else {
          debugAssert(
            () => openVersion > savedVersion,
            'Expected saved version to be behind the open version'
          );
          this.permanentWarn('Reconnected. Saving...');
        }
        notifications.close(this.secondaryWarningId());
        this.unsaveableWarningShown = false;
      }
      this.staleCount = 0;
    } else {
      this.staleCount++;
      // Show a warning if we have had MAX_STALE consecutive ticks without a save happening.
      if (this.staleCount === MAX_STALE) {
        this.staleWarningShown = true;
        filename = this.friendlyFileNameWithStyling();
        // Show just long enough until the other error / success notification comes in.
        analytics.track('auto_save', 'slow_save', {file_id: this.id, type: 'error'});
        if (this.showErrors) {
          this.permanentWarn(
            <>
              Still saving...
              <br />
              This might take a while if your connection is slow or if your {filename} is large. Don't refresh
              -- you could lose your work.
            </>
          );
        }
      }
    }
    // Attempt save to LocalStorage / server
    if (shouldSave) {
      this.throttledQueueSave();
    } else {
      // We just had a successful save, so we should update localStorage. This is to prevent the
      // case where the user closes the page immediately after the successful save, and we have yet
      // update local storage.
      this.saveToLocalStorage();
    }
  }

  queueSave(isMandatory?: boolean): JQueryPromise<boolean> {
    // When a file is closed, it is likely that there is one throttled queueSave in progress, so we ignore it.
    if (this._getItemState() == null && this.isStopped) {
      return $.Deferred<boolean>().resolve(false).promise();
    }

    // Save to LocalStorage in case saving to server fails.
    this.saveToLocalStorage();

    // If the tick is stopped, that means we notified the user that we were unable to complete the save
    // and have stopped saving. If they have reached here, it means they are ignoring the message, so we give
    // them a gentle reminder.
    if (!this.isTicking()) {
      analytics.track('auto_save', 'type_without_save', {file_id: this.id});
      notifications.warn('Your file is not saving. Copy your work and refresh the page.', {
        id: this.secondaryWarningId(),
      });
      return $.Deferred<boolean>().resolve(false).promise();
    }

    // Attempt a save if we are not up-to-date.
    const wrappedDoSave = (): JQueryPromise<boolean> => {
      // If the autosaver is stopped, drop the save
      if (this.isStopped) {
        this.promiseChainLength--;
        return $.Deferred<boolean>().resolve(false).promise();
      }
      const promiseOrVoid = this._doSave();
      // We always expect a JQuery promise to be returned, but this guards against a un-typed caller that mistakenly
      // provides a non-promise return value. All typed stores will return a JQuery promise.
      if (promiseOrVoid) {
        return (
          promiseOrVoid
            // We need both the then and fail callbacks to decrement the promiseChainLength and convert from a
            // boolean return value to a void return value. The queue will have JQueryPromise<void> but each
            // individual save promise will return a true/false boolean indicating if the save successfully occurred.
            .done((result) => {
              this.promiseChainLength--;
              return result;
            })
            .fail((e) => {
              debugAssert(() => false, 'AutoSaver save failed', {error: e, fileId: this.id});
              this.stop();
            })
        );
      } else {
        logging.error("AutoSaver._doSave() didn't return a promise", {
          extra: {
            fileId: this.id,
          },
        });
        this.promiseChainLength--;
        return $.Deferred<boolean>().resolve(false).promise();
      }
    };

    // If concurrent save prevention is enabled and savePromiseChain exists and is not yet resolved,
    // and (either a mandatory save is required or if there are 2 or less promises in the chain),
    // then chain the next save after the current one finishes.
    // The length is limited to 2 since there can be 1 save promise currently being resolved by the server and
    // 1 save promise waiting in the chain to be sent. When each save starts, it will collect the most recent
    // state of the document from the store. Therefore, we should never need to have more than two promises chained.
    // If the user makes another update, it will automatically be included in the payload sent by the currently queued save.
    // If the save is not mandatory and the current save promise is unresolved (i.e. a save is in flight),
    // drop the non-mandatory save
    // If the promise chain does not exist, assign it to the return value of wrappedDoSave.
    // If concurrent save prevention is disabled, we will always attempt to save immediately.
    if (
      BenchlingConfig.ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES &&
      this.savePromiseChain &&
      (this.promiseChainLength < 2 || isMandatory)
    ) {
      this.promiseChainLength++;
      this.savePromiseChain = this.savePromiseChain.then(wrappedDoSave);
    } else if (!BenchlingConfig.ELN_PREVENT_CONCURRENT_NOTEBOOK_SAVES || !this.savePromiseChain) {
      this.promiseChainLength = 1;
      this.savePromiseChain = wrappedDoSave();
    }
    return this.savePromiseChain;
  }

  /**
   * Notifies the user that the AutoSaver is unable to save their file. It provides a link for the user to
   * click to resume the tick. If will also send the drafts dump along with the error message for the first
   * pause after a completed save.
   *
   * @param  {Object} extra  A JSON-serializable object to be logged to the server. If extra.error is passed
   *                         and contains a friendlyError, it is assumed that the caller has already notified
   *                         the user of the error.
   */
  pause<T extends {status: number | null; downtime?: boolean; error?: SaveResponse}>(
    extra: T | null,
    param?: {skipClientDump?: boolean | null; lockedFromExternalChange?: boolean | null}
  ): void {
    let message: string | JSX.Element;
    if (extra == null) {
      extra = {status: null, error: undefined, downtime: undefined} as T;
    }
    if (param == null) {
      param = {};
    }
    const {skipClientDump, lockedFromExternalChange} = param;
    const didStop = this.stopTick();
    if (!didStop) {
      // If there is a save queued up after one ends up being a 500 (that triggers AutoSaver.pause), then the
      // next request is still fired, which is likely to fail if the underlying issue is not resolved,
      // causing a second redundant call to pause. Technically, we can avoid this if the caller checks to see
      // if the AutoSaver is stopped before firing off the next request, but that would require a non-trivial
      // refactor.
      return;
    }

    // lockedFromExternalChange is unrecoverable, so we don't give the user false hope.
    if (lockedFromExternalChange) {
      this.saveToLocalStorage(); // Register the draft as having been lockedFromExternalChange
      return;
    }

    const filename = this.friendlyFileNameWithStyling();
    if (!this.unsaveableWarningShown) {
      // Only re-log after user reconnects
      analytics.track('auto_save', 'pause', {file_id: this.id, status: extra.status});
      if (!skipClientDump && shouldClientDump(extra.status, {csrfFailed: extra.error?.csrfFailed})) {
        drafts.postClientDump(
          extra,
          extra.status,
          (extra.error != null ? extra.error.userMessage : undefined) != null
            ? extra.error != null
              ? extra.error.userMessage
              : undefined
            : 'error'
        );
      }
    }

    this.unsaveableWarningShown = true;

    if (extra.status === 503 && extra.downtime) {
      message = `The system is currently undergoing maintenance. Please copy your work and try again later.`;
    } else if (extra.error != null && notifications.getFriendlyError(extra.error) != null) {
      const baseMessage = <>Your {filename} is unable to save.</>;
      if (extra.error.noResave) {
        message = baseMessage;
      } else {
        // If the user was already presented a friendlyError message, then hopefully they have enough
        // context to fix the issue (e.g. a CSRF error will already indicate the user needs to sign in again).
        message = (
          <>
            {baseMessage} <a href={this.resumeHash()}>Click here to try the save again.</a>
          </>
        );
      }
    } else if (extra.status === 401) {
      message = (
        <>
          You're not signed in! Changes to {filename} won't be saved. <a href={SIGNIN_URL}>Sign in.</a>
        </>
      );
    } else {
      message = (
        <>
          A recent change to your {filename} is unexpectedly preventing it from saving.{' '}
          <a href={this.resumeHash()}>Click here to try the save again</a>, or copy your work, refresh the
          page, and try re-applying your changes. If the issue persists, contact support@benchling.com for
          help.
        </>
      );
    }

    this.permanentError(message);
  }

  // Call to attempt the save again. If the save is successful, the user will be notified.
  private resume(): void {
    const didStart = this.startTick();
    if (!didStart) {
      debugAssert(() => false, 'Attempted to resume already running AutoSaver', {fileId: this.id});
      return;
    }
    // Reset error state to allow future resume attempts
    this.unsaveableWarningShown = false;
    this.staleWarningShown = false;
    const filename = this.friendlyFileNameWithStyling();
    this.permanentWarn(<>Retrying save for {filename}...</>);
  }

  resumeIfStopped(): void {
    if (!this.isTicking()) {
      this.resume();
    }
  }

  stop(): void {
    this.isStopped = true;
    this.stopTick();
    $(window).off(this.unloadEvent);
    $(document).off(this.resumeClickEvent);
    this.savePromiseChain = null;
    this.promiseChainLength = 0;
  }
}

export default AutoSaver;
